// Die Bild-Matrix des Varianten-Reiters: Zeilen sind Bilder, Spalten sind Varianten, und in
// der Zelle steht die Anzeige-Reihenfolge dieses Bildes fuer diese Variante.
//
// Bewusst DOM- und DB-frei (Muster: `lib/publish-button.ts`, `worker/lib/price-diff.ts`).
// Entwurf: docs/superpowers/specs/2026-08-12-kaufland-bildgrid-varianten-design.md
//
// WARUM ES DAS BRAUCHT: Am 2026-08-12 hingen alle neun freigegebenen Bilder am
// Variantenvater, beide Kinder hatten null. Bei Kaufland ist der Vater kein Produkt --
// gelistet werden die Kinder, und die gingen ohne Bild raus, was Kaufland mit
// `MISSING_ATTRIBUTES: ["Bild"]` quittiert. Diese Matrix verteilt die Bilder des Vaters auf
// die Kinder.
//
// ABBILDUNG AUF DIE DATENBANK: Eine Zelle ist genau eine Zeile in
// `kaufland.article_allowed_images` (Primaerschluessel tenant + Artikel + Resource), die Zahl
// darin ihr `sort_order`. Deshalb reicht hier eine Liste je Spalte: Der Index IST die
// Reihenfolge, und ein Loch kann konstruktionsbedingt nicht entstehen.

/** Spalte (Artikel-ID) -> Bilder in Anzeigereihenfolge (Resource-IDs). */
export type GridState = Record<string, string[]>;

export type MoveDirection = 'hoch' | 'runter';

/** Die Bilder einer Spalte in ihrer Reihenfolge. */
export function orderedResources(state: GridState, column: string): string[] {
  return [...(state[column] ?? [])];
}

/**
 * Ordnet ein Bild einer Spalte zu — es haengt hinten an und bekommt damit die naechste
 * freie Nummer.
 *
 * Verlangt ausdruecklich NICHT, dass das Bild schon zu diesem Artikel gehoert: Der haeufigste
 * Fall ist ein Bild des Vaters, das einem Kind zugeordnet wird. Genau dafuer gibt es den
 * Reiter.
 */
export function assignImage(state: GridState, column: string, resourceId: string): GridState {
  const vorhanden = state[column] ?? [];
  if (vorhanden.includes(resourceId)) return state;
  return { ...state, [column]: [...vorhanden, resourceId] };
}

/**
 * Entfernt die Zuordnung. Die uebrigen ruecken lueckenlos nach.
 *
 * Das Nachruecken ist der Grund, warum diese Logik nicht im Renderer stehen darf: Ein Loch in
 * der Reihenfolge faellt in der Oberflaeche nicht auf, bei Kaufland aber schon.
 */
export function removeImage(state: GridState, column: string, resourceId: string): GridState {
  const vorhanden = state[column] ?? [];
  if (!vorhanden.includes(resourceId)) return state;
  return { ...state, [column]: vorhanden.filter((r) => r !== resourceId) };
}

/** Verschiebt ein Bild innerhalb seiner Spalte. An den Raendern passiert nichts. */
export function moveImage(
  state: GridState,
  column: string,
  resourceId: string,
  direction: MoveDirection,
): GridState {
  const vorhanden = [...(state[column] ?? [])];
  const i = vorhanden.indexOf(resourceId);
  if (i < 0) return state;
  const ziel = direction === 'hoch' ? i - 1 : i + 1;
  if (ziel < 0 || ziel >= vorhanden.length) return state;
  [vorhanden[i], vorhanden[ziel]] = [vorhanden[ziel]!, vorhanden[i]!];
  return { ...state, [column]: vorhanden };
}

/**
 * Setzt ein Bild auf alle uebergebenen Spalten — der „alle"-Knopf einer Bildzeile.
 *
 * Der Aufrufer uebergibt die Spalten ausdruecklich, statt dass die Funktion `state` durchgeht:
 * Die Vater-Spalte ist im Zustand enthalten (sie zeigt, woher die Bilder stammen), darf aber
 * nie mitgesetzt werden. Der Vater ist bei Kaufland kein Produkt; eine Freigabe an ihm waere
 * eine Zeile fuer etwas, das nie gelistet wird.
 */
export function assignToAll(state: GridState, columns: readonly string[], resourceId: string): GridState {
  let next = state;
  for (const c of columns) next = assignImage(next, c, resourceId);
  return next;
}

/**
 * Welche Spalten gespeichert werden muessen.
 *
 * Nicht bloss Sparsamkeit: `kaufland-release-images` loest je Release einen
 * `vario.ensure_dms_share`-Job aus. Eine unveraenderte Spalte mitzuschicken erzeugt echte
 * Worker-Last ohne jede Wirkung.
 *
 * Eine reine Umsortierung zaehlt als Aenderung — bei Kaufland ist das die Reihenfolge, in der
 * die Bilder am Produkt erscheinen.
 */
export function changedColumns(before: GridState, after: GridState): string[] {
  const spalten = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: string[] = [];
  for (const c of spalten) {
    const a = before[c] ?? [];
    const b = after[c] ?? [];
    if (a.length !== b.length || a.some((r, i) => r !== b[i])) out.push(c);
  }
  return out.sort();
}
